import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import DOMPurify from 'dompurify';
import { Plus, FileCheck, Eye, Printer, Mail, X, Building2, Stethoscope, FlaskConical } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import api from '@/lib/axios';
import { presignUrl, openS3File } from '@/lib/storage';
import { useAuthStore } from '@/store/auth.store';
import { ConsentimientoWizard } from './ConsentimientoWizard';
import { getErrorMessage } from '@/lib/errors';
import {
  TAMANO_INFO, DISENO_INFO, getDimensiones,
  type TemplateData,
} from '../configuracion/templates/template-types';
import { TemplateRenderer } from '../configuracion/templates/TemplateRenderer';
import { usePlantillasConfig, DEFAULT_PLANTILLAS_CONFIG } from '../configuracion/templates/usePlantillaConfig';

// ─── Types ───────────────────────────────────────────────────────────────────

type TipoConsentimiento   = 'clinica' | 'medico' | 'tratamiento';
type EstadoConsentimiento = 'pendiente' | 'firmado' | 'revocado';

const TIPO_INFO: Record<TipoConsentimiento, { label: string; icon: typeof Building2; cls: string }> = {
  clinica:     { label: 'Clínica',     icon: Building2,   cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  medico:      { label: 'Médico',      icon: Stethoscope, cls: 'bg-violet-50 text-violet-700 border-violet-200' },
  tratamiento: { label: 'Tratamiento', icon: FlaskConical, cls: 'bg-teal-50 text-teal-700 border-teal-200' },
};

const ESTADO_INFO: Record<EstadoConsentimiento, { label: string; cls: string }> = {
  pendiente: { label: 'Pendiente', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  firmado:   { label: 'Firmado',   cls: 'bg-green-50 text-green-700 border-green-200' },
  revocado:  { label: 'Revocado',  cls: 'bg-red-50 text-red-500 border-red-200' },
};

function formatDate(d: string | Date) {
  return new Date(d).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
}

// ─── Firma preview (S3 privado vía presigned URL) ─────────────────────────────

function esPdf(url: string) {
  return /\.pdf($|\?)/i.test(url);
}

function FirmaPreview({ firmaUrl }: { firmaUrl: string }) {
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const esPdfFile = esPdf(firmaUrl);

  useEffect(() => {
    presignUrl(firmaUrl).then(setSignedUrl).catch(() => setSignedUrl(null));
  }, [firmaUrl]);

  return (
    <div>
      <p className="text-xs font-medium text-text-secondary mb-2">Firma del paciente</p>
      {esPdfFile ? (
        <button
          type="button"
          onClick={() => openS3File(firmaUrl)}
          className="flex items-center gap-1.5 text-xs text-primary-600 hover:text-primary-800 border border-gray-200 rounded-xl px-3 py-2 transition-colors">
          <FileCheck size={13} /> Ver documento firmado (PDF)
        </button>
      ) : (
        <div className="border border-gray-200 rounded-xl overflow-hidden bg-white inline-block min-h-[3rem] min-w-[6rem]">
          {signedUrl
            ? <img src={signedUrl} alt="Firma" className="max-h-28 w-auto" />
            : <p className="text-xs text-text-secondary p-3">Cargando firma…</p>}
        </div>
      )}
    </div>
  );
}

// ─── Print modal ──────────────────────────────────────────────────────────────

function ConsentimientoPrintModal({ consent, paciente, clinica, usuario, onClose }: {
  consent: any; paciente: any; clinica: any; usuario: any; onClose: () => void;
}) {
  const printRef = useRef<HTMLDivElement>(null);
  const { data: plantillasConfig } = usePlantillasConfig();
  const cfg = (plantillasConfig ?? DEFAULT_PLANTILLAS_CONFIG).consentimiento;
  const { width: pw, height: ph, cssSize } = getDimensiones(cfg.tamanoHoja, cfg.orientacion);
  const SCALE = 0.72;
  const modalW = Math.round(pw * SCALE);
  const modalH = Math.round(ph * SCALE);

  const [firmaSignedUrl, setFirmaSignedUrl] = useState<string | null>(null);
  const firmaEsPdf = consent.firmaUrl ? esPdf(consent.firmaUrl) : false;

  useEffect(() => {
    if (consent.firmaUrl && !firmaEsPdf) {
      presignUrl(consent.firmaUrl).then(setFirmaSignedUrl).catch(() => setFirmaSignedUrl(null));
    }
  }, [consent.firmaUrl, firmaEsPdf]);

  const nombre = [paciente?.nombre, paciente?.apellidoPaterno, paciente?.apellidoMaterno].filter(Boolean).join(' ');

  const data: TemplateData = {
    tipo: 'consentimiento',
    folio: consent.id?.slice(0, 8).toUpperCase() || '—',
    fecha: formatDate(consent.createdAt || new Date()),
    generadoPor: consent.medico?.nombre || usuario?.nombre,
    clinica: {
      nombre:    clinica?.nombre       || 'Clínica',
      cedula:    clinica?.cedulaFiscal || '',
      direccion: clinica?.direccion    || '',
      telefono:  clinica?.telefono     || '',
    },
    medico: {
      nombre:       consent.medico?.nombre || usuario?.nombre || 'Médico',
      cedula:       consent.medico?.cedula || '',
      especialidad: consent.medico?.especialidad || '',
    },
    paciente: {
      nombre,
      fechaNacimiento: paciente?.fechaNacimiento ? formatDate(paciente.fechaNacimiento) : '',
      sexo: paciente?.genero ? ({ masculino: 'Masculino', femenino: 'Femenino', otro: 'Otro' }[paciente.genero as string] || '') : '',
    },
    fechaConsulta: formatDate(consent.createdAt || new Date()),
    consentimientoTitulo:    consent.titulo || 'Consentimiento informado',
    consentimientoContenido: consent.contenido,
    firmaUrl:  firmaEsPdf ? consent.firmaUrl : (firmaSignedUrl ?? undefined),
    firmaEsPdf,
    firmadoEn: consent.firmadoEn ? formatDate(consent.firmadoEn) : undefined,
  };

  function handlePrint() {
    const el = printRef.current;
    if (!el) return;
    const css = `*{margin:0;padding:0;box-sizing:border-box}body{background:#fff}@media print{@page{size:${cssSize};margin:0}}`;
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(`<!DOCTYPE html><html><head><style>${css}</style></head><body>${DOMPurify.sanitize(el.innerHTML)}</body></html>`);
    w.document.close();
    w.onload = () => w.print();
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center overflow-y-auto py-8 px-4">
      <div className="bg-gray-50 rounded-2xl shadow-2xl w-full max-w-5xl">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 px-4 md:px-6 py-3 md:py-4 bg-white rounded-t-2xl border-b border-gray-100">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-semibold text-sm text-text-primary">{consent.titulo || 'Consentimiento'}</h2>
            <span className={cn('text-[10px] font-semibold px-2 py-0.5 rounded-full border',
              (ESTADO_INFO[(consent.estado as EstadoConsentimiento)] ?? ESTADO_INFO.pendiente).cls
            )}>{(ESTADO_INFO[(consent.estado as EstadoConsentimiento)] ?? ESTADO_INFO.pendiente).label}</span>
            <span className="text-xs text-text-secondary bg-gray-100 px-2 py-0.5 rounded-full">{TAMANO_INFO[cfg.tamanoHoja].label}</span>
            <span className="text-xs text-text-secondary bg-gray-100 px-2 py-0.5 rounded-full">{DISENO_INFO[cfg.diseno].label}</span>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button onClick={handlePrint}
              className="flex items-center gap-1.5 text-xs bg-primary-600 text-white px-3 py-1.5 rounded-lg hover:bg-primary-700 transition-colors">
              <Printer size={12} /> Imprimir
            </button>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-text-secondary transition-colors">
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Template preview */}
        <div className="p-3 md:p-6 overflow-x-auto">
          <div style={{ width: modalW, height: modalH, overflow: 'hidden', boxShadow: '0 4px 32px rgba(0,0,0,0.18)', borderRadius: 4, margin: '0 auto' }}>
            <div ref={printRef} style={{ width: pw, height: ph, transform: `scale(${SCALE})`, transformOrigin: 'top left', pointerEvents: 'none', userSelect: 'none' }}>
              <TemplateRenderer data={data} diseno={cfg.diseno} width={pw} height={ph} disenoPersonalizadoHtml={cfg.disenoPersonalizadoHtml} />
            </div>
          </div>
        </div>

        <div className="flex justify-between items-center px-6 py-4 bg-white rounded-b-2xl border-t border-gray-100">
          <p className="text-xs text-text-secondary">Plantilla configurada en Configuración → Plantillas</p>
          <button onClick={() => { toast.info('Envío por correo electrónico próximamente'); }}
            className="flex items-center gap-1.5 text-xs border border-gray-200 px-3 py-1.5 rounded-lg hover:bg-gray-50 text-text-secondary transition-colors">
            <Mail size={12} /> Enviar por correo
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Detail modal ─────────────────────────────────────────────────────────────

function ConsentimientoModal({ consent, onClose, onPrint }: { consent: any; onClose: () => void; onPrint: () => void }) {
  const tipoInfo   = TIPO_INFO[consent.tipo as TipoConsentimiento]   || TIPO_INFO.clinica;
  const estadoInfo = ESTADO_INFO[consent.estado as EstadoConsentimiento] || ESTADO_INFO.pendiente;
  const Icon = tipoInfo.icon;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[85vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2.5">
            <span className={cn('flex items-center justify-center w-8 h-8 rounded-lg border', tipoInfo.cls)}>
              <Icon size={15} />
            </span>
            <div>
              <p className="font-semibold text-text-primary text-sm leading-tight">{consent.titulo || 'Consentimiento'}</p>
              <div className="flex items-center gap-2 mt-0.5">
                <span className={cn('text-[10px] font-semibold px-1.5 py-0.5 rounded-full border', tipoInfo.cls)}>{tipoInfo.label}</span>
                <span className={cn('text-[10px] font-semibold px-1.5 py-0.5 rounded-full border', estadoInfo.cls)}>{estadoInfo.label}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <button onClick={onPrint}
              className="flex items-center gap-1.5 text-xs bg-primary-600 text-white px-2.5 py-1.5 rounded-lg hover:bg-primary-700 transition-colors">
              <Printer size={12} /> Imprimir
            </button>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-text-secondary transition-colors">
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <div className="text-xs text-text-secondary">
            Creado: <span className="font-medium text-text-primary">{formatDate(consent.createdAt)}</span>
            {consent.firmadoEn && (
              <> · Firmado: <span className="font-medium text-text-primary">{formatDate(consent.firmadoEn)}</span></>
            )}
          </div>

          <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
            <pre className="text-xs text-text-primary whitespace-pre-wrap font-sans leading-relaxed">
              {consent.contenido}
            </pre>
          </div>

          {consent.firmaUrl && <FirmaPreview firmaUrl={consent.firmaUrl} />}
        </div>
      </div>
    </div>
  );
}

// ─── Main Tab ─────────────────────────────────────────────────────────────────

interface Props {
  id: string;
  canEdit: boolean;
  paciente?: any;
}

export function ConsentimientosTab({ id, canEdit, paciente }: Props) {
  const qc = useQueryClient();
  const { usuario } = useAuthStore();
  const [wizardOpen,   setWizardOpen]   = useState(false);
  const [viewing,      setViewing]      = useState<any | null>(null);
  const [printing,     setPrinting]     = useState<any | null>(null);

  const { data: clinica } = useQuery({
    queryKey: ['clinica', usuario?.clinicaId],
    queryFn: () => api.get(`/api/clinicas/${usuario!.clinicaId}`).then(r => r.data),
    enabled: !!usuario?.clinicaId,
    staleTime: 300_000,
  });

  const { data: consentimientos = [], isLoading } = useQuery({
    queryKey: ['consentimientos-paciente', id],
    queryFn: () => api.get(`/api/pacientes/${id}/consentimientos`).then(r => r.data as any[]),
  });

  const revocarMutation = useMutation({
    mutationFn: (cId: string) => api.patch(`/api/consentimientos/${cId}/revocar`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['consentimientos-paciente', id] });
      toast.success('Consentimiento revocado');
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al revocar')),
  });

  function closeWizard() {
    setWizardOpen(false);
    qc.invalidateQueries({ queryKey: ['consentimientos-paciente', id] });
  }

  return (
    <>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <p className="text-xs text-text-secondary">
            <span className="font-semibold text-text-primary">{consentimientos.length}</span>{' '}
            consentimiento{consentimientos.length !== 1 ? 's' : ''}
          </p>
          {canEdit && (
            <button
              onClick={() => setWizardOpen(true)}
              className="flex items-center gap-1.5 bg-primary-600 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-primary-700 transition-colors">
              <Plus size={13} /> Nuevo consentimiento
            </button>
          )}
        </div>

        {/* List */}
        {isLoading ? (
          <p className="text-center py-10 text-sm text-text-secondary">Cargando...</p>
        ) : consentimientos.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-xl border border-dashed border-gray-200">
            <FileCheck size={28} className="mx-auto text-gray-300 mb-2" />
            <p className="text-sm text-text-secondary">Sin consentimientos registrados</p>
            {canEdit && (
              <button onClick={() => setWizardOpen(true)}
                className="mt-3 text-xs text-primary-600 hover:underline">
                Agregar primer consentimiento
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {consentimientos.map((c: any) => {
              const tipoInfo   = TIPO_INFO[c.tipo as TipoConsentimiento]   || TIPO_INFO.clinica;
              const estadoInfo = ESTADO_INFO[c.estado as EstadoConsentimiento] || ESTADO_INFO.pendiente;
              const Icon = tipoInfo.icon;

              return (
                <div key={c.id} className="rounded-xl border border-gray-100 bg-white p-4 space-y-2.5">
                  {/* Top */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2.5">
                      <span className={cn('flex items-center justify-center w-8 h-8 rounded-lg border flex-shrink-0', tipoInfo.cls)}>
                        <Icon size={14} />
                      </span>
                      <div>
                        <p className="text-sm font-semibold text-text-primary leading-tight">
                          {c.titulo || 'Consentimiento'}
                        </p>
                        <span className={cn('text-[10px] font-semibold px-1.5 py-0.5 rounded-full border', tipoInfo.cls)}>
                          {tipoInfo.label}
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      <span className={cn('text-[10px] font-semibold px-2 py-0.5 rounded-full border', estadoInfo.cls)}>
                        {estadoInfo.label}
                      </span>
                      <span className="text-xs text-text-secondary">{formatDate(c.createdAt)}</span>
                    </div>
                  </div>

                  {/* Firma preview */}
                  {c.firmaUrl && c.estado === 'firmado' && (
                    <div className="flex items-center gap-2 bg-green-50 rounded-lg px-3 py-1.5 border border-green-100">
                      <FileCheck size={12} className="text-green-600 flex-shrink-0" />
                      <p className="text-xs text-green-700">Firmado el {formatDate(c.firmadoEn)}</p>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex items-center gap-3 pt-1 border-t border-gray-50">
                    <button onClick={() => setViewing(c)}
                      className="flex items-center gap-1 text-[10.5px] text-text-secondary hover:text-text-primary transition-colors">
                      <Eye size={11} /> Ver contenido
                    </button>
                    <button onClick={() => setPrinting(c)}
                      className="flex items-center gap-1 text-[10.5px] text-text-secondary hover:text-text-primary transition-colors">
                      <Printer size={11} /> Imprimir
                    </button>
                    {canEdit && c.estado === 'firmado' && (
                      <button
                        onClick={() => { if (confirm('¿Revocar este consentimiento?')) revocarMutation.mutate(c.id); }}
                        className="flex items-center gap-1 text-[10.5px] text-red-400 hover:text-red-600 transition-colors ml-auto">
                        <X size={11} /> Revocar
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Wizard */}
      {wizardOpen && (
        <ConsentimientoWizard
          pacienteId={id}
          paciente={paciente}
          onClose={closeWizard}
        />
      )}

      {/* Detail modal */}
      {viewing && (
        <ConsentimientoModal
          consent={viewing}
          onClose={() => setViewing(null)}
          onPrint={() => { setPrinting(viewing); setViewing(null); }}
        />
      )}

      {/* Print modal */}
      {printing && (
        <ConsentimientoPrintModal
          consent={printing}
          paciente={paciente}
          clinica={clinica}
          usuario={usuario}
          onClose={() => setPrinting(null)}
        />
      )}
    </>
  );
}
